import React, { useState } from 'react';
import { toast } from 'sonner';
import smsService from '../../services/smsService';
import { X, Phone, CheckCircle, HourglassMedium, Envelope } from '@phosphor-icons/react';
import './AnnouncementSMSModal.css';

function AnnouncementSMSModal({ isOpen, onClose, announcement, phoneNumbers, onSuccess, loading = false }) {
  const [sendLoading, setSendLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen || !announcement) return null;

  const handleSend = async () => {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      toast.error('No phone numbers provided');
      return;
    }

    setSendLoading(true);
    try {
      const response = await smsService.sendAnnouncement({
        announcement_id: announcement.id,
        phone_numbers: phoneNumbers,
        title: announcement.title,
        snippet: announcement.content?.substring(0, 100)
      });

      if (response.success) {
        toast.success(`SMS sent to ${phoneNumbers.length} recipient(s)!`);
        setConfirmed(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
          setConfirmed(false);
        }, 2000);
      }
    } catch (err) {
      toast.error('Failed to send SMS: ' + err.message);
    } finally {
      setSendLoading(false);
    }
  };

  const messageSample = `📢 ${announcement.title}: ${announcement.content?.substring(0, 50)}...`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal announcement-sms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Phone size={24} />
            Send Announcement SMS
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {confirmed ? (
          <div className="modal-body success-state">
            <div className="success-icon"><CheckCircle size={24} /></div>
            <h3>SMS Sent Successfully!</h3>
            <p>Announcement sent to {phoneNumbers.length} recipient(s)</p>
          </div>
        ) : (
          <>
            <div className="modal-body">
              {loading ? (
                <div className="loading-state" style={{ textAlign: 'center', padding: '20px' }}>
                  <p><HourglassMedium size={18} /> Loading recipient list...</p>
                </div>
              ) : (
                <>
              {/* Announcement Info */}
              <div className="info-section">
                <h3>Announcement Details</h3>
                <div className="info-row">
                  <span className="info-label">Title:</span>
                  <span className="info-value">{announcement.title}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Recipients:</span>
                  <span className="info-value recipients">{phoneNumbers.length} contacts</span>
                </div>
              </div>

              {/* Recipients List */}
              <div className="recipients-section">
                <h3>Recipients ({phoneNumbers.length})</h3>
                <div className="recipients-list">
                  {phoneNumbers.slice(0, 5).map((phone, idx) => (
                    <div key={idx} className="recipient-item">
                      <Phone size={14} />
                      {phone}
                    </div>
                  ))}
                  {phoneNumbers.length > 5 && (
                    <div className="recipient-item more">
                      +{phoneNumbers.length - 5} more
                    </div>
                  )}
                </div>
              </div>

              {/* Message Preview */}
              <div className="message-section">
                <h3><Envelope size={18} /> SMS Message Preview</h3>
                <div className="message-box">
                  <p>{messageSample}</p>
                </div>
                <p className="message-hint">This is how the SMS will appear to recipients</p>
              </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={sendLoading || loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="btn btn-primary"
                disabled={sendLoading || loading || phoneNumbers.length === 0}
              >
                {sendLoading ? (<><HourglassMedium size={18} /> Sending...</>) : (<><CheckCircle size={18} /> Send SMS</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AnnouncementSMSModal;
